<script setup lang="ts">
//#region imports
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { endOfDay, format, startOfDay } from 'date-fns';
import { useApi } from '@/api';
import { useLs } from '@/setup/vueLs';
import { useTypedStore } from '@/store';
import { useDetectPermissions } from '@/composables/useDetectPermissions';
import { useEventBus } from '@/setup/eventBus';
import { useFilters } from '@/setup/filters';
import { useTablePaginator, useTableSearch, useTableSort } from '@/composables/useTableLS';
import { usePopupManager } from '@/components/PopupManager.vue';
import UiPagination from '@/components/UIElements/UiPagination.vue';
import updateUrl from '@/service/updateUrl';
import _ from '@/setup/lodash';
import { useFormatters } from '@/composables/useFormatters';
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';
import type { GetPaymentsHistoryCurrenciesResponse, GetPaymentsHistoryData, GetPaymentsHistoryResponse } from '@/types/openapi-ts';
import type { TableMethods } from '@/types/core';
//#endregion

//#region types
type PaymentsHistoryRow = GetPaymentsHistoryResponse['payload']['data'][number];
type PaymentsHistoryPermissions = NonNullable<GetPaymentsHistoryResponse['misc']>['permissions'];
type PaymentsHistoryQuery = NonNullable<GetPaymentsHistoryData['query']>;
type HistoryCurrency = GetPaymentsHistoryCurrenciesResponse['payload'][number];
//#endregion

//#region const
const sizes = {
  1080: 20,
  1440: 30,
};

const route = useRoute();
const fromPage = ref(route.query.from || '');
//#endregion

//#region composables
const api = useApi();
const popupManager = usePopupManager();
const ls = useLs();
const store = useTypedStore();
const { t } = useI18n();
const { checkRequest, checkRoute } = useDetectPermissions();
const eventBus = useEventBus();
const filters = useFilters();
const { formatCurrencyValue } = useFormatters();

const fields = computed<UiTableNewField<PaymentsHistoryRow>[]>(() => [
  {
    name: 'incrementing_id',
    align: 'left',
    sortable: 'custom',
    width: '70',
    fixed: 'left',
  },
  ...(fromPage.value === 'payment_dashboard' ? [{
    name: 'type',
    align: 'left',
    width: '100',
    computedValue: getPaymentTypeLabel,
  }] : []),
  {
    name: 'affiliate_email',
    align: 'left',
    minWidth: '200',
    sortable: 'custom',
    link: getAffiliateLink,
  },
  {
    name: 'team_name',
    width: '120',
    minWidth: '120',
  },
  {
    name: 'approved_by_admin_name',
    align: 'left',
    minWidth: '200',
    sortable: 'custom',
    overflow: true,
  },
  {
    name: 'amount',
    align: 'right',
    width: '92',
    class: 'bold',
    sortable: 'custom',
    computedValue: (row) => formatCurrencyValue(row.currency_code, row.amount),
    computedClass: getCurrencyClass,
  },
  {
    name: 'available_balance',
    align: 'right',
    width: '92',
    sortable: 'custom',
    computedValue: (row) => formatCurrencyValue(row.balance_currency_code, row.available_balance),
    computedClass: getCurrencyClass,
  },
  {
    name: 'hold_balance',
    align: 'right',
    width: '92',
    sortable: 'custom',
    computedValue: (row) => formatCurrencyValue(row.balance_currency_code, row.hold_balance),
    computedClass: getCurrencyClass,
  },
  {
    name: 'payment_period_end',
    align: 'left',
    width: '170',
    sortable: 'custom',
    computedValue: computePaymentPeriod,
  },
  {
    name: 'created_at',
    width: '120',
    align: 'left',
    headerAlign: 'left',
    sortable: 'custom',
    format: 'date-time',
    filterable: true,
    allowedOperators: ['≤', '=', '≥'],
  },
  {
    name: 'site_payment_method_name',
    align: 'center',
    headerAlign: 'center',
    width: '120',
    minWidth: '120',
    class: 'text',
    computedImg: (row) => row.site_payment_method_logo_base64,
    computedImgNull: (_, row) => row.site_payment_method_name,
  },
  {
    name: 'payment_wallet',
    align: 'left',
    headerAlign: 'center',
    width: '120',
    minWidth: '120',
    copyValue: true,
  },
]);

const { limit, count, page, setPage, setLsPathPage } = useTablePaginator({
  lsPath: getLsPath(),
  sizes,
  defaultSize: 15,
});

const { sortProp, sortOrder, setSort, optionsSort, setLsPath } = useTableSort({
  initialOptions:{
    sort_column: 'incrementing_id',
    sort_dir: 'desc',
  },
  lsPath: getLsPath(),
  fields: fields.value,
});

const { searchQuery, setSearch } = useTableSearch();
//#endregion

//#region state
const tableRef = ref<TableMethods | null>(null);
const currentPaymentPageStatus = ref('');
const currencyCode = ref('');
const historyCurrencies = ref<HistoryCurrency[]>([]);
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
const tableData = ref<PaymentsHistoryRow>([]);
const permissions = ref<PaymentsHistoryPermissions>({});
const sitePaymentMethodId = ref('');
const paymentType = ref('');

const columnsFilter = ref({});
const createdAtFrom = ref<string>();
const createdAtTo = ref<string>();

const reportDate = ref('');
//#endregion

//#region computed
const adminAcl = computed(() => store.getters['auth/adminAcl']);
const currentAcl = computed(() => store.getters['auth/currentAcl']);
const paymentMethods = computed(() => store.getters['misc/paymentMethods']);
const lsPath = computed(() => getLsPath());
const allCurrencies = computed(() => [
  { name: t('settings.currencies.allCurrencies'), code: '' },
  ...historyCurrencies.value,
]);
const filterParams = computed(() => {
  const params: Record<string, string> = {};
  if (fromPage.value === 'payment_dashboard' && paymentType.value) {
    params.type = paymentType.value;
  }
  if (sitePaymentMethodId.value) {
    params.site_payment_method_id = sitePaymentMethodId.value;
  }
  return params;
});
const filtersList = computed(() => {
  const list = [];

  if (fromPage.value === 'payment_dashboard') {
    list.push({
      id: 'type',
      name: 'paymentType',
      type: 'select',
      params: {
        list: [
          { id: 'internal', name: t('payments.types.internal') },
          { id: 'external', name: t('payments.types.external') },
        ],
      },
    });
  }

  list.push({
    id: 'site_payment_method_id',
    name: 'sitePaymentMethod',
    type: 'select',
    params: {
      list: paymentMethods.value,
    },
  });

  return list;
});
const breadcrumbs = computed(() => {
  return [
    {
      title: t('payments.history.payments_dashboard'),
      link: '/payments',
    },
    {
      title: `${t('payments.history.paidAt')} ${filters.formatDate4(reportDate.value)}`,
    },
  ];
});
//#endregion

//#region methods
function resolveCurrencyCode(code?: string | null) {
  if (code && historyCurrencies.value.some((currency) => currency.code === code)) {
    return code;
  }

  return '';
}

async function initData() {
  currentPaymentPageStatus.value = route.params.status as string;

  if (fromPage.value && fromPage.value === 'payment_dashboard') {
    checkHistory();
  }

  if (checkRequest('payments_view')){
    const response = await api.getPaymentsHistoryCurrencies();
    historyCurrencies.value = response.data.payload || [];

    const _urlData = updateUrl.getParseParams(route.hash);
    if (!_.isEmpty(_urlData)) {
      setPage(_urlData);
      columnsFilter.value = _.get(_urlData, ['columns_filter']) || {};

      delete _urlData.page;
      delete _urlData.limit;
      delete _urlData.columns_filter;

      _.merge(optionsSort.value, _urlData);

      setSort(_urlData);
      setSearch(_urlData);

      const currency_code = _.get(_urlData, ['currency_code']);
      if(currency_code) {
        currencyCode.value = resolveCurrencyCode(currency_code as string);
      }
      sitePaymentMethodId.value = _.get(_urlData, ['site_payment_method_id'], '') as string;
      if (fromPage.value === 'payment_dashboard') {
        paymentType.value = _.get(_urlData, ['type'], '') as string;
      }
    } else {
      const currency_code = ls.get(`${lsPath.value}/currency_code`);
      if(currency_code) {
        currencyCode.value = resolveCurrencyCode(currency_code as string);
      }
      columnsFilter.value = ls.get(`${lsPath.value}/columns_filter`) || {};
    }

    parseFilter(columnsFilter.value, _.isEmpty(columnsFilter.value) === true ? 'reset' : undefined);

    nextTick(() => {
      fetchData();
    });
  }
  checkRoute('payments_view');
}

const _query = () => {
  let result = {
    ...optionsSort.value,
    search: searchQuery.value,
    limit: limit.value,
    offset: limit.value * page.value - limit.value,
    moderation_status: 'approved',
    payout_status: currentPaymentPageStatus.value === 'paid'
      ? ['paid', 'cancelled']
      : currentPaymentPageStatus.value,
    paid_at_from: reportDate.value ? format(reportDate.value, 'yyyy-MM-dd 00:00:00') : '',
    paid_at_to: reportDate.value ? format(reportDate.value, 'yyyy-MM-dd 23:59:59') : '',
    currency_code: currencyCode.value,
    site_payment_method_id: sitePaymentMethodId.value,
    created_at_from: createdAtFrom.value,
    created_at_to: createdAtTo.value,
  } as PaymentsHistoryQuery;

  const type = fromPage.value === 'payment_dashboard' ? paymentType.value: 'internal';

  if (type) {
    result.type = type as PaymentsHistoryQuery['type'];
  }

  if(sortProp.value === 'affiliate_referred_by_info') {
    result.sort_column = 'affiliate_referred_by_email';
  }

  return result;
};

const _completedFilterUrl = () => {
  const _dataFilters = {
    ..._.omit(_query(), ['offset', 'created_at_from', 'created_at_to']),
    columns_filter: columnsFilter.value,
  };
  updateUrl.updateGetParams(_dataFilters);
};

async function fetchData(silent = false) {
  if (!silent && !isFirstLoading.value) {
    isDataLoading.value = true;
  }

  _completedFilterUrl();
  let options = _query();

  try {
    const { data: { payload, misc } } = await api.getPaymentsHistory(options);

    count.value = misc!.count;
    permissions.value = misc!.permissions;
    tableData.value = payload.data;
  } finally {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }

    if (tableRef.value) {
      tableRef.value.setColumnFilters(columnsFilter.value);
    }
  }
}

function checkHistory() {
  reportDate.value = route.query.date as string;
  currentPaymentPageStatus.value = 'paid';
}

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

const handleRefresh = () => {
  page.value = 1;
  fetchData();
};

const handlePaymentsFilter = (data: Record<string, string> = {}) => {
  paymentType.value = data.type || '';
  sitePaymentMethodId.value = data.site_payment_method_id || '';
  page.value = 1;
  fetchData();
};

function changeCurrency(value: string) {
  currencyCode.value = value;
  ls.set(`${lsPath.value}/currency_code`, value);
  fetchData();
}

function handlerFilter(filter: any, action: string) {
  parseFilter(filter, action);
  fetchData();
}

function parseFilter(filter: any, action?: string) {
  const columnProp = 'created_at';
  if (action === 'reset') {
    createdAtFrom.value = undefined;
    createdAtTo.value = undefined;
    columnsFilter.value = {};
    return;
  }

  if (filter[columnProp].op === '≥') {
    createdAtFrom.value = format(startOfDay(filter[columnProp].dateRange),'yyyy-MM-dd HH:mm:ss');
    createdAtTo.value = undefined;
  } else if (filter[columnProp].op === '≤') {
    createdAtFrom.value = undefined;
    createdAtTo.value = format(endOfDay(filter[columnProp].dateRange),'yyyy-MM-dd HH:mm:ss');
  } else {
    createdAtFrom.value = format(startOfDay(filter[columnProp].dateRange[0]),'yyyy-MM-dd HH:mm:ss');
    createdAtTo.value = format(endOfDay(filter[columnProp].dateRange[1]),'yyyy-MM-dd HH:mm:ss');
  }
  ls.set(`${lsPath.value}/columns_filter`, filter);
  columnsFilter.value = filter;
}

function openPaymentEstimates(currencyCode: string) {
  popupManager.paymentEstimates.open({ data: { currencyCode } });
}

function getLsPath() {
  return `PaymentsPayout/${route.params.status}`;
}

function getAffiliateLink(row: PaymentsHistoryRow) {
  return `/affiliates/${row.affiliate_id}/info`;
}

function getCurrencyClass(value: number) {
  return value < 0 ? 'negative' : '';
}

function handleRowClick(row: PaymentsHistoryRow) {
  return `/payments/info/${row.id}?from=payments_payout`;
}

function reviewActions(row: PaymentsHistoryRow) {
  const isUser = !adminAcl.value.is_superuser && permissions.value && permissions.value[row.id]!.review_actions;
  const isSuperUser = adminAcl.value.is_superuser;
  return isSuperUser || isUser;
}

function computePaymentPeriod(row: PaymentsHistoryRow) {
  return row.payment_period_start && row.payment_period_end
    ? `${filters.formatDate4(row.payment_period_start)} - ${filters.formatDate4(row.payment_period_end)}`
    : '';
}

function getPaymentTypeLabel(row: PaymentsHistoryRow) {
  return row.type ? t(`payments.types.${row.type}`) : '';
}

function calcWidth() {
  const page = currentPaymentPageStatus.value === 'not_paid';
  const isUser = !adminAcl.value.is_superuser && page && currentAcl.value.payments_review_actions === 'allow';
  const isSuperUser = adminAcl.value.is_superuser && page;
  return isUser || isSuperUser ? 105 : 76;
}

function reviewHandler(row: PaymentsHistoryRow, e: MouseEvent) {
  const context = popupManager.confirm.open({
    data: {
      title: t('payments.confirm.title_3'),
      message: t('payments.confirm.msg_3'),
      bodyIcon: 'exclamation-triangle',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('payments.confirm.action_3'),
      confirmType: 'positive',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const confirm = await context;
        await reviewPayment(row);
        confirm.handleClose();
      },
    },
  });
}

function declineHandler(row: PaymentsHistoryRow, e: MouseEvent) {
  const context = popupManager.confirm.open({
    data: {
      title: t('payments.confirm.title_2'),
      message: t('payments.confirm.msg_2', { affiliateEmail: row.affiliate_email }),
      bodyIcon: 'exclamation-triangle',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('payments.confirm.action_2'),
      confirmType: 'negative',
      cancelTitle: t('affiliates.cardView.toolbar.btns.close'),
    },
    listeners: {
      success: async () => {
        const confirm = await context;
        await declinePayment(row);
        confirm.handleClose();
      },
    },
  });
}

function declinePayment(row: PaymentsHistoryRow) {
  isDataLoading.value = true;
  return api
    .declinePayment(row.id)
    .then(() => {
      fetchData();
    })
    .catch((error) => {
      isDataLoading.value = false;
      eventBus.$emit('notify-event', {
        type: 'error',
        text: t('payments.noty.req_fail', { statusCode: error.status }),
      });
    });
}

function reviewPayment(row: PaymentsHistoryRow) {
  isDataLoading.value = true;
  return api
    .reviewPayment(row.id)
    .then(() => {
      fetchData();
    })
    .catch((error) => {
      isDataLoading.value = false;
      eventBus.$emit('notify-event', {
        type: 'error',
        text: t('payments.noty.req_fail', { statusCode: error.status }),
      });
    });
}
//#endregion

//#region watch
watch(lsPath, (value) => {
  fromPage.value = route.query.from || '';
  currentPaymentPageStatus.value = route.params.status as string;
  reportDate.value = '';
  currencyCode.value = '';

  if (fromPage.value === 'payment_dashboard') {
    checkHistory();
  }

  setLsPath(value);
  setLsPathPage(value);

  searchQuery.value = '';

  const currency = ls.get(`${lsPath.value}/currency_code`);
  if (currency !== '' && currency !== null) {
    currencyCode.value = resolveCurrencyCode(ls.get(`${lsPath.value}/currency_code`));
  }

  parseFilter(columnsFilter.value, _.isEmpty(columnsFilter.value) === true ? 'reset' : undefined);
  nextTick(() => {
    fetchData();
  });
  popupManager.$scopeDispose();
});
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div class="payments-payout">
    <Breadcrumbs
      v-if="fromPage === 'payment_dashboard'"
      :items="breadcrumbs"
    />
    <UiToolbar>
      <template v-slot:title>
        <template v-if="fromPage === 'payment_dashboard'">
          {{ `${$t('payments.history.paidAt')} ${$filters.formatDate4(reportDate)}` }}
        </template>
        <template v-else>
          <div class="t:flex t:items-center t:gap-8">
            {{ $t('payments.page_title.payments_payout.name') }}
            <UiSeparator
              type="dot"
            />
            <UiStatusText
              :type="currentPaymentPageStatus"
              :text="$t(`payments.page_title.payments_payout.${currentPaymentPageStatus}`)"
              :font-size="20"
            />
          </div>
        </template>
      </template>
      <template v-slot:left-slot>
        <UiInput
          v-model.trim="searchQuery"
          :width="180"
          is-search
          @search="handleRefresh"
        />
        <DefaultFilter
          :model-value="filterParams"
          :filters-list="filtersList"
          @submit="handlePaymentsFilter"
        />
      </template>
      <template v-slot:right-slot>
        <UiCurrencyPicker
          :key="lsPath"
          :model-value="currencyCode"
          :currencies="allCurrencies"
          :disabled="isDataLoading"
          @update:model-value="changeCurrency"
        />
        <UiButton
          v-if="currentPaymentPageStatus === 'not_paid'"
          is-icon
          icon="database"
          @click="openPaymentEstimates(currencyCode)"
        />
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
    </UiToolbar>
    <UiTableNew
      ref="tableRef"
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :currency="currencyCode"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="payments.history"
      @sort="handleSort"
      @filter="handlerFilter"
    >
      <template v-slot:append>
        <ElTableColumn
          :resizable="false"
          :width="calcWidth()"
          fixed="right"
          align="center"
          header-align="center"
        >
          <template v-slot:header="scope">
            <div
              :key="scope.$index"
              class="table-action-header"
            >
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              icon="eye"
              :tooltip="$t('payments.history.info')"
              :router-to="handleRowClick(scope.row)"
            />
            <ActionIcon
              v-if="reviewActions(scope.row) && currentPaymentPageStatus === 'not_paid'"
              icon="check-double"
              :tooltip="$t('payments.history.toReview')"
              @click="reviewHandler(scope.row, $event)"
            />
            <ActionIcon
              v-if="reviewActions(scope.row) && currentPaymentPageStatus === 'not_paid'"
              icon="times"
              color="var(--text-negative)"
              :tooltip="$t('payments.history.decline')"
              @click="declineHandler(scope.row, $event)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>
